import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EnvironmentService } from '@commonspirit/ngx-environment';
import { ActionsSubject } from '@ngrx/store';
import { catchError, concatMap, map, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import {
  ApiResponse,
  GeolocationSuggestionResult,
  GeolocationByIpResult,
  GeolocationDetailResult,
  SelectedGeolocation
} from '../../models/v2/search';
import { EnvValue } from '@commonspirit/ngx-environment/lib/models/key-value-pair';
import { v4 as uuid } from 'uuid';
import { InstanceService } from '@commonspirit/ngx-instance';
import { ConfigurationKey } from 'src/app/core/models/configuration-key';
import { ILocationAutoSuggSearchParams } from '../../models/v1/location';
import { LocationByIpResult } from 'src/app/core/models/location';

@Injectable({
  providedIn: 'root'
})
export class GeolocationService {
  public falApiBaseUrl: string;
  public providerSearchApiBaseUrl: string;
  public apimSubscriptionSecretKey: EnvValue;
  private _apiOptions: { headers: HttpHeaders };
  private _apimOptions: { headers: HttpHeaders };
  private _geolocationSessionIdKey = 'fal-geolocation-sessionId';

  constructor(
    public http: HttpClient,
    public actionsSubject: ActionsSubject,
    public environmentService: EnvironmentService,
    public instanceService: InstanceService
  ) {
    const falApiBaseUrlForEnvironment: string | undefined = this.environmentService.getEndpoint('mulesoftLocationsApi')?.url;
    if (falApiBaseUrlForEnvironment) {
      this.falApiBaseUrl = falApiBaseUrlForEnvironment;
    } else {
      throw new Error('FAL API base URL cannot be resolved.');
    }

    const apimSubscriptionKey: EnvValue | undefined = this.environmentService.getSetting('apimSubscriptionKey')?.value;
    if (apimSubscriptionKey) {
      this.apimSubscriptionSecretKey = apimSubscriptionKey;
    } else {
      throw new Error('APIM base URL cannot be resolved.');
    }

    const providerSearchApiBaseUrlForEnvironment: string | undefined = this.environmentService.getEndpoint('providerSearchApi')?.url;
    if (providerSearchApiBaseUrlForEnvironment) {
      this.providerSearchApiBaseUrl = providerSearchApiBaseUrlForEnvironment;
    } else {
      throw new Error('Provider Search API base URL cannot be resolved.');
    }

    this._apiOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };

    this._apimOptions = {
      headers: new HttpHeaders({
        'ocp-apim-subscription-key': this.apimSubscriptionSecretKey.toString()
      })
    };
  }

  getLocationByIP(): Observable<ApiResponse<LocationByIpResult>> {
    const locationDetailsApi = '/geolocation';
    const LOCATION_DETAILS_API = `${this.falApiBaseUrl}${locationDetailsApi}`;
    return this.http.get<ApiResponse<LocationByIpResult>>(`${LOCATION_DETAILS_API}`, this._apiOptions);
  }

  getGeolocationSuggestions(query: string): Observable<ApiResponse<GeolocationSuggestionResult[]>> {
    const zipPattern = /\d+/g;
    if (query?.match(zipPattern) && query?.split(',').length < 3) {
      query = query.replace(/^\D+/g, '').trim();
    } else {
      query = query.trim();
    }
    const token = uuid();
    let httpParams = new HttpParams();
    if (token) {
      httpParams = httpParams.append('sessionToken', token.trim());
    }
    if (this.instanceService.getConfiguration(ConfigurationKey.LocationAutoSuggSearchURLParams)) {
      httpParams = this.autoSuggLocationSearchParams(
        this.instanceService.getConfiguration(ConfigurationKey.LocationAutoSuggSearchURLParams)?.value as ILocationAutoSuggSearchParams,
        httpParams
      );
    }

    httpParams = httpParams.append('searchQuery', query.replace(/\s/g, ''));
    const url = `${this.falApiBaseUrl}/suggestions/locations`;
    return this.http.get<ApiResponse<GeolocationSuggestionResult[]>>(url, {
      headers: this._apiOptions.headers,
      params: httpParams
    });
  }

  getGeolocationByCoordinates(latitude: number, longitude: number): Observable<ApiResponse<GeolocationByIpResult>> {
    return this.http.get<ApiResponse<GeolocationByIpResult>>(
      `${this.providerSearchApiBaseUrl}/location/${latitude},${longitude}`,
      this._apimOptions
    );
  }

  getGeolocationDetail(googlePlaceId: string): Observable<ApiResponse<GeolocationDetailResult>> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('locationId', googlePlaceId);
    const url = `${this.falApiBaseUrl}/suggestions/location-detail`;

    return this.http
      .get<ApiResponse<GeolocationDetailResult>>(url, {
        headers: this._apiOptions.headers,
        params: httpParams
      })
      .pipe(
        tap((response: ApiResponse<GeolocationDetailResult>) => {
          if (response.result) {
            window.sessionStorage.setItem(this._geolocationSessionIdKey, response.result.sessionToken);
          }
        })
      );
  }

  public queryLocation(location: string): Observable<SelectedGeolocation | null> {
    if (!location) {
      return of(null);
    }
    return this.getGeolocationSuggestions(location).pipe(
      catchError(() => of(null)),
      concatMap((suggestionResponse: ApiResponse<GeolocationSuggestionResult[]> | null) => {
        if (!suggestionResponse || !suggestionResponse.result || suggestionResponse.result.length === 0) {
          return of({ isLocationValid: false } as SelectedGeolocation);
        }
        const zipPattern = /\d+/g;

        let googlePlaceId = '';
        const isZipExist = location.match(zipPattern);
        if (isZipExist) {
          googlePlaceId = suggestionResponse.result[0].googlePlaceId;
        } else {
          googlePlaceId =
            suggestionResponse.result.find((s) =>
              s.locationName
                .replace(/[,.]|\s/g, '')
                .toLowerCase()
                .trim()
                .includes(
                  location
                    .replace(/[,.]|-|\s/g, '')
                    .toLowerCase()
                    .trim()
                )
            )?.googlePlaceId ?? '';
        }
        return this.getGeolocationDetail(googlePlaceId).pipe(
          map((detailResponse) => {
            return this.mapToSelectedGeolocation(googlePlaceId, detailResponse.result, isZipExist);
          }),
          catchError(() => of(null))
        );
      })
    );
  }

  public mapToSelectedGeolocation(
    googlePlaceId: string,
    geolocationDetailResult: GeolocationDetailResult,
    isZip: RegExpMatchArray | null
  ): SelectedGeolocation {
    return {
      googlePlaceId: googlePlaceId,
      name: geolocationDetailResult.name + (isZip && geolocationDetailResult.zip ? ' ' + geolocationDetailResult.zip : ''),
      neighborhood: geolocationDetailResult.neighborhood ? geolocationDetailResult.neighborhood : '',
      city: geolocationDetailResult.city,
      state: geolocationDetailResult.state,
      latitude: geolocationDetailResult.latitude,
      longitude: geolocationDetailResult.longitude,
      zip: isZip ? geolocationDetailResult.zip : '',
      isLocationValid: true
    } as SelectedGeolocation;
  }

  private autoSuggLocationSearchParams(_suggParams: ILocationAutoSuggSearchParams, httpParams: HttpParams): HttpParams {
    // All three params are mandatory as per US
    if (_suggParams.latitude && _suggParams.longitude && _suggParams.radius) {
      httpParams = httpParams.set('latitude', _suggParams.latitude?.toString().trim());
      httpParams = httpParams.set('longitude', _suggParams.longitude?.toString().trim());
      httpParams = httpParams.set('radius', _suggParams.radius?.toString().trim());
      httpParams = httpParams.set('strictbounds', true);
    }
    return httpParams;
  }

}
